import { api, el, toast } from "./dom";
import { icon } from "./icons";
import { openModal } from "./modal";
import { download, ib } from "./record";
import { openSend } from "./send";
import { readingState } from "./textbox";

// The file viewer (brief 1B-9, mockup v23 attModal): every attachment and every file on a record
// opens here, inside FlowCare, never as a download. Images and PDFs are drawn in the browser from a
// short-lived, view-only address, with Zoom in, Zoom out and Rotate (a PDF shows each page with
// "n of N"); anything else shows its name and size. From an inbox attachment a person decides:
// File it (What it is, File to; AI thinks pre-fills both when AI and Reading uploads are on),
// Forward or send, Move to Scans to file, or Leave it. Download shows only with the Download
// files switch. At phone width it's a full sheet with the actions under the pages.

interface Opened {
  file: { id: string; name: string; type: string; size: number; parts: number };
  kind: "image" | "pdf" | "other";
  url: string;
  line: string;
  canDownload: boolean;
  canFile: boolean;
}
interface Choice {
  key: string;
  name: string;
  to: ("client" | "person")[];
  credential: boolean;
}
interface Filing {
  choices: Choice[];
  filed: string[];
  inScans: boolean;
}
interface Target {
  kind: "client" | "person";
  id: string;
  name: string;
  line?: string;
}

export interface ViewerOptions {
  file: { id: string; name: string };
  /** The document's name, for the Send sheet ("Service agreement"). */
  document?: string;
  /** After File it or Scans to file, so the screen can show it. */
  onDone?: () => void;
}

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];

const size = (n: number) =>
  n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

/** The file's bytes from its view address, part by part (files over 4 MB come in parts). */
async function bytesOf(o: Opened): Promise<Uint8Array | null> {
  const parts: Uint8Array[] = [];
  for (let n = 0; n < o.file.parts; n++) {
    const res = await fetch(n ? `${o.url}?part=${n}` : o.url, { cache: "no-store" });
    if (!res.ok) return null;
    parts.push(new Uint8Array(await res.arrayBuffer()));
  }
  const all = new Uint8Array(parts.reduce((t, p) => t + p.length, 0));
  let at = 0;
  for (const p of parts) {
    all.set(p, at);
    at += p.length;
  }
  return all;
}

// pdf.js (Mozilla, Apache-2.0) draws PDF pages; it's served beside the app (scripts/build-app.mjs)
// and loaded only when a PDF is opened.
interface PdfPage {
  getViewport(o: { scale: number; rotation: number }): { width: number; height: number };
  render(o: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: unknown;
  }): { promise: Promise<void> };
}
interface PdfDoc {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
}
interface PdfJs {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(o: {
    data: Uint8Array;
    wasmUrl: string;
    standardFontDataUrl: string;
    cMapUrl: string;
    cMapPacked: boolean;
    iccUrl: string;
  }): { promise: Promise<PdfDoc> };
}
let pdfjs: Promise<PdfJs> | null = null;
function loadPdfJs(): Promise<PdfJs> {
  const src = "/vendor/pdfjs/pdf.min.js";
  pdfjs ??= (import(/* @vite-ignore */ src) as Promise<PdfJs>).then((m) => {
    m.GlobalWorkerOptions.workerSrc = "/vendor/pdfjs/pdf.worker.min.js";
    return m;
  });
  return pdfjs;
}

/** Opens a file in the viewer. */
export async function openViewer(o: ViewerOptions): Promise<void> {
  const res = await api<Opened>("POST", `/api/files/${encodeURIComponent(o.file.id)}/view`);
  if (!res.ok) return toast("That file can't be opened", res.body.error);
  const v = res.body;

  // ---- The pages ----
  let zoom = 2;
  let turn = 0;
  const pages = el("div", { class: "attpages", "aria-label": `Pages of ${v.file.name}` });
  const zoomIn = ib("plus", "Zoom in", () => setZoom(1));
  const zoomOut = ib("dash", "Zoom out", () => setZoom(-1));
  const rotate = ib("sync", "Rotate", () => {
    turn = (turn + 90) % 360;
    void draw();
  });
  const tools = el("div", { class: "atttools" }, zoomIn, zoomOut, rotate);
  const pane = el("div", { class: "attpane" }, tools, pages);
  function setZoom(step: number) {
    zoom = Math.max(0, Math.min(ZOOMS.length - 1, zoom + step));
    zoomIn.disabled = zoom === ZOOMS.length - 1;
    zoomOut.disabled = zoom === 0;
    void draw();
  }

  let image: HTMLImageElement | null = null;
  let doc: PdfDoc | null = null;
  let drawing = 0;
  async function draw() {
    const scale = ZOOMS[zoom] ?? 1;
    if (image) {
      image.style.width = `${scale * 100}%`;
      image.style.transform = turn ? `rotate(${turn}deg)` : "";
      return;
    }
    if (!doc) return;
    const mine = ++drawing;
    const width = Math.max(240, pages.clientWidth - 28);
    const out: HTMLElement[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);
      if (mine !== drawing) return;
      const base = page.getViewport({ scale: 1, rotation: turn });
      const viewport = page.getViewport({ scale: (width / base.width) * scale, rotation: turn });
      const ratio = window.devicePixelRatio || 1;
      const canvas = el("canvas", {
        width: String(Math.floor(viewport.width * ratio)),
        height: String(Math.floor(viewport.height * ratio)),
        style: `width:${Math.floor(viewport.width)}px;height:${Math.floor(viewport.height)}px`,
        role: "img",
        "aria-label": `Page ${n} of ${doc.numPages}`,
      });
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      ctx.scale(ratio, ratio);
      await page.render({ canvasContext: ctx, canvas, viewport }).promise;
      if (mine !== drawing) return;
      out.push(
        el(
          "figure",
          { class: "attpage" },
          canvas,
          el("figcaption", { class: "small muted" }, `${n} of ${doc.numPages}`),
        ),
      );
    }
    pages.replaceChildren(...out);
  }

  const sub = el(
    "span",
    { class: "small muted", style: "display:block" },
    [v.kind === "image" ? "Photo" : null, v.line].filter(Boolean).join(" · "),
  );

  // ---- What to do with it ----
  const side = el("div", { class: "attside" });
  const closeAnd = (fn?: () => void) => {
    m.close();
    fn?.();
  };
  const forward = ib("paper-airplane", "Forward or send", () =>
    closeAnd(
      () => void openSend({ files: [{ id: v.file.id, name: v.file.name }], document: o.document }),
    ),
  );
  const leave = ib("x", "Leave it", () => {
    void api("POST", `/api/files/${v.file.id}/leave`);
    closeAnd();
  });
  const others: HTMLElement[] = [forward];
  if (v.canFile) {
    const scans = ib("file-badge", "Move to Scans to file", async () => {
      scans.disabled = true;
      const r = await api("POST", `/api/files/${v.file.id}/scan`);
      if (!r.ok) {
        scans.disabled = false;
        return toast("That wasn't moved", r.body.error);
      }
      closeAnd(o.onDone);
      toast("Moved to Scans to file", "It waits there to be filed");
    });
    others.push(scans);
  }
  others.push(leave);
  if (v.canDownload)
    others.push(
      ib("download", `Download ${v.file.name}`, () => void download(v.file.id, v.file.name)),
    );
  const below = el(
    "div",
    { class: "attor" },
    v.canFile ? el("span", { class: "small muted" }, "Or") : null,
    el("div", { class: "rowacts" }, ...others),
    v.canDownload
      ? null
      : el(
          "span",
          { class: "small muted" },
          icon("lock"),
          ' Download is off for this role. Admins can allow it in Users and roles ("Download files").',
        ),
  );

  const m = openModal({
    title: v.file.name,
    body: [
      el(
        "div",
        { class: "attgrid" },
        v.kind === "other"
          ? el(
              "div",
              { class: "attpane attother" },
              icon("file", "big"),
              el("b", {}, v.file.name),
              el(
                "span",
                { class: "small muted" },
                `${size(v.file.size)} · This kind of file isn't shown here.`,
              ),
            )
          : pane,
        side,
      ),
    ],
    className: "viewer",
  });
  side.append(below);
  // The line under the title, as in the mockup: "3 pages · from … · when".
  const h2 = m.root.querySelector(".cardhead h2");
  if (h2) {
    const wrap = el("div", { style: "min-width:0;flex:1" });
    h2.replaceWith(wrap);
    wrap.append(h2, sub);
  }

  if (v.canFile) void fileForm(v, side, below, m.close, o.onDone);

  // ---- Load the file ----
  if (v.kind !== "other") {
    pages.append(el("p", { class: "small muted" }, "Opening…"));
    const bytes = await bytesOf(v);
    if (!bytes) {
      pages.replaceChildren(
        el("p", { class: "note", role: "alert" }, "The file couldn't be opened."),
      );
      return;
    }
    if (v.kind === "image") {
      const url = URL.createObjectURL(new Blob([bytes.slice()], { type: v.file.type }));
      image = el("img", { src: url, alt: v.file.name, class: "attimg" });
      pages.replaceChildren(el("figure", { class: "attpage" }, image));
      void draw();
    } else {
      try {
        const lib = await loadPdfJs();
        // The helper folders pdf.js needs for scanned and faxed pages (JBIG2, CCITT fax and
        // JPEG 2000 images), fonts the PDF doesn't carry, character maps and color profiles.
        doc = await lib.getDocument({
          data: bytes,
          wasmUrl: "/vendor/pdfjs/wasm/",
          standardFontDataUrl: "/vendor/pdfjs/standard_fonts/",
          cMapUrl: "/vendor/pdfjs/cmaps/",
          cMapPacked: true,
          iccUrl: "/vendor/pdfjs/iccs/",
        }).promise;
        const n = doc.numPages;
        sub.textContent = [`${n} page${n === 1 ? "" : "s"}`, v.line].filter(Boolean).join(" · ");
        await draw();
      } catch {
        pages.replaceChildren(
          el("p", { class: "note", role: "alert" }, "This PDF couldn't be shown."),
        );
      }
    }
  }
}

/** File it: What it is and File to, AI's guess when it's on, and where it's filed already. */
async function fileForm(
  v: Opened,
  side: HTMLElement,
  below: HTMLElement,
  close: () => void,
  onDone?: () => void,
) {
  const got = await api<Filing>("GET", `/api/files/${v.file.id}/filing`);
  if (!got.ok) return;
  const f = got.body;
  const what = el(
    "select",
    { id: "att-what" },
    el("option", { value: "" }, "Pick one"),
    ...f.choices.map((c) => el("option", { value: c.key }, c.name)),
  );
  const find = el("input", {
    id: "att-to",
    type: "text",
    autocomplete: "off",
    placeholder: "Name, email or phone",
  });
  const picks = el("div", { class: "picks", role: "list", "aria-label": "Records found" });
  const hint = el("span", { class: "hint" });
  let target: Target | null = null;
  const choice = () => f.choices.find((c) => c.key === what.value) ?? null;
  const say = () => {
    const c = choice();
    hint.textContent = c?.credential
      ? "It goes to their documents as Submitted and waits in Inbox › Documents for review."
      : c
        ? "It goes to that record's documents."
        : "";
  };
  const pick = (t: Target) => {
    target = t;
    find.value = t.name;
    picks.replaceChildren();
  };
  what.addEventListener("change", say);
  let n = 0;
  find.addEventListener("input", async () => {
    target = null;
    const mine = ++n;
    const q = find.value.trim();
    if (q.length < 2) return picks.replaceChildren();
    const r = await api<{ records: Target[] }>(
      "GET",
      `/api/inbox/records?edit=1&q=${encodeURIComponent(q)}`,
    );
    if (mine !== n) return;
    const found = (r.ok ? r.body.records : []).filter(
      (x) => !choice() || choice()?.to.includes(x.kind),
    );
    picks.replaceChildren(
      ...(found.length
        ? found.map((x) => {
            const b = el(
              "button",
              { type: "button", class: "btn", role: "listitem" },
              icon(x.kind === "client" ? "organization" : "person"),
              " ",
              el("b", {}, x.name),
              x.line ? el("span", { class: "small muted" }, ` · ${x.line}`) : "",
            );
            b.addEventListener("click", () => pick(x));
            return b;
          })
        : [el("p", { class: "small muted" }, "No editable client or person matches.")]),
    );
  });

  const note = el("div", { class: "note", role: "alert", hidden: true });
  const fileIt = el("button", { type: "button", class: "btn pri" }, icon("check"), " File it");
  fileIt.addEventListener("click", async () => {
    note.hidden = true;
    const c = choice();
    const refuse = (s: string) => {
      note.replaceChildren(icon("alert"), " ", s);
      note.hidden = false;
    };
    if (!c) return refuse("Say what it is.");
    if (!target) return refuse("Pick the record to file it to.");
    fileIt.disabled = true;
    const r = await api<{ filed: string; credential: boolean }>(
      "POST",
      `/api/files/${v.file.id}/file`,
      { as: c.key, kind: target.kind, id: target.id },
    );
    fileIt.disabled = false;
    if (!r.ok) return refuse(r.body.error ?? "That wasn't filed.");
    close();
    toast(
      r.body.filed,
      r.body.credential ? "Waiting in Inbox › Documents for review" : "It's on the record",
    );
    onDone?.();
  });

  const ai = el("div", { class: "note", hidden: true });
  const form = el(
    "form",
    { class: "form", style: "max-width:none" },
    el("div", { class: "field" }, el("label", { for: "att-what" }, "What it is"), what),
    el("div", { class: "field" }, el("label", { for: "att-to" }, "File to"), find, picks, hint),
    note,
    el("div", { class: "actions" }, fileIt),
  );
  form.addEventListener("submit", (e) => e.preventDefault());
  const filed = f.filed.length
    ? el(
        "ul",
        { class: "attfiled small" },
        ...f.filed.map((l) => el("li", {}, icon("check-circle"), ` ${l}`)),
      )
    : null;
  const scan = f.inScans
    ? el("p", { class: "small muted" }, icon("file-badge"), " In Scans to file")
    : null;
  side.insertBefore(el("div", { class: "attfile" }, ai, filed ?? "", scan ?? "", form), below);
  if (f.inScans) below.querySelector('[aria-label="Move to Scans to file"]')?.remove();

  // AI thinks: only when AI and Reading uploads are on; it only fills the fields.
  if (readingState() !== "on" || v.kind === "other") return;
  const s = await api<{
    suggestion: { as: string | null; record: Target | null; line: string } | null;
  }>("POST", "/api/ai/file", { file: v.file.id });
  if (!s.ok || !s.body.suggestion || !side.isConnected) return;
  const g = s.body.suggestion;
  ai.replaceChildren(el("p", {}, icon("light-bulb"), " ", el("b", {}, "AI thinks:"), ` ${g.line}`));
  ai.hidden = false;
  if (g.as && !what.value) {
    what.value = g.as;
    say();
  }
  if (g.record && !target && !find.value) pick(g.record);
}
